// Void a sale.
//
// Three ways in, decided by the server (void_transaction): a permission
// (transactions.cancel) voids directly; otherwise the owner's PIN typed here at
// the till; otherwise an approved void request for this invoice. The dialog
// shows whichever applies. The database is the authority on whether a void is
// allowed at all (no returns, no recorded payments, event and lots still open);
// its message names the invoice and the reason, so it is shown verbatim.

import { useEffect, useState } from 'react';
import { Ban, KeyRound, ShieldCheck, Send, Clock } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useVoidTransaction } from '@/hooks/useTransactions';
import { useRequestApproval } from '@/hooks/useApprovals';
import { formatIDR, formatDateTime, cn } from '@/lib/format';
import type { AppTransaction } from '@/types/domain';
import type { ApprovalRequest } from '@/lib/api/approvals';

export interface VoidTransactionDialogProps {
  open: boolean;
  onClose: () => void;
  transaction: AppTransaction;
  /** Holds transactions.cancel — voids without PIN or slip. */
  canDirect: boolean;
  /** The latest void request for this invoice, if any. */
  request?: ApprovalRequest | null;
  onVoided?: () => void;
}

type Mode = 'pin' | 'request';

export function VoidTransactionDialog({
  open,
  onClose,
  transaction,
  canDirect,
  request,
  onVoided,
}: VoidTransactionDialogProps) {
  const voidTx = useVoidTransaction();
  const requestApproval = useRequestApproval();
  const [reason, setReason] = useState('');
  const [pin, setPin] = useState('');
  const [mode, setMode] = useState<Mode>('pin');
  const [error, setError] = useState<string | null>(null);

  const approved = request?.status === 'approved' && !request.consumedAt ? request : null;
  const pending = request?.status === 'pending' ? request : null;

  useEffect(() => {
    if (!open) return;
    setReason(approved?.reason ?? pending?.reason ?? '');
    setPin('');
    setMode('pin');
    setError(null);
  }, [open, approved, pending]);

  const withSlip = !canDirect && !!approved;
  const needsPin = !canDirect && !approved && mode === 'pin';
  const asRequest = !canDirect && !approved && mode === 'request';

  const reasonOk = reason.trim().length >= 3;
  const canSubmit =
    reasonOk && (asRequest ? !pending : !needsPin || /^\d{4,6}$/.test(pin));

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    try {
      if (asRequest) {
        await requestApproval.mutateAsync({
          kind: 'transaction.void',
          reason,
          payload: {
            transaction_id: transaction.id,
            invoice_number: transaction.invoiceNumber,
            amount: transaction.grandTotal,
            customer: transaction.customerSnapshot?.name ?? null,
          },
          eventId: transaction.eventId,
        });
        onClose();
        return;
      }
      await voidTx.mutateAsync({
        id: transaction.id,
        reason,
        ownerPin: needsPin ? pin : undefined,
      });
      onVoided?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Void gagal');
      setPin('');
    }
  };

  const busy = voidTx.isPending || requestApproval.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={asRequest ? 'Ajukan void?' : 'Void transaksi?'}
      description={`${transaction.invoiceNumber} · ${formatIDR(transaction.grandTotal)}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Batal
          </Button>
          <Button
            variant={asRequest ? 'primary' : 'danger'}
            onClick={submit}
            loading={busy}
            disabled={!canSubmit}
            leftIcon={asRequest ? <Send size={16} /> : <Ban size={16} />}
          >
            {asRequest ? 'Kirim permintaan' : 'Void'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="rounded border border-warning/30 bg-warning/5 px-3 py-2 text-caption text-ink-subtle space-y-1">
          <p>
            Stok setiap item <b>kembali ke lot asalnya</b>, status jadi <b>Void</b>, dan
            transaksi keluar dari semua laporan. Nomor invoice tetap tercatat.
          </p>
          <p>
            Tidak bisa di-void kalau sudah ada retur (pakai retur), sudah ada pembayaran
            tercatat (DP/cicilan), atau event/lot-nya sudah ditutup.
          </p>
          {transaction.openclawStatus === 'sent' && (
            <p>Invoice ini sudah terkirim ke WhatsApp customer — beri tahu customernya secara terpisah.</p>
          )}
        </div>

        <Textarea
          label="Alasan (wajib)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          autoFocus
          disabled={withSlip}
          placeholder="Contoh: salah input qty, customer batal"
        />

        {canDirect ? (
          <p className="inline-flex items-center gap-1.5 text-caption text-success">
            <ShieldCheck size={14} />
            Kamu punya hak batalkan invoice — void langsung disetujui atas namamu.
          </p>
        ) : withSlip ? (
          <p className="inline-flex items-start gap-1.5 text-caption text-success">
            <ShieldCheck size={14} className="mt-0.5 shrink-0" />
            <span>
              Permintaan void invoice ini <b>sudah disetujui</b>
              {approved?.resolvedAt ? ` (${formatDateTime(approved.resolvedAt)})` : ''}. Klik Void
              untuk menjalankannya — persetujuan dipakai sekali.
            </span>
          </p>
        ) : (
          <>
            <div className="inline-flex rounded border border-line bg-surface p-0.5">
              {(
                [
                  { id: 'pin', label: 'PIN owner', icon: <KeyRound size={13} /> },
                  { id: 'request', label: 'Minta persetujuan', icon: <Send size={13} /> },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setMode(opt.id)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-caption font-medium rounded transition-colors',
                    mode === opt.id ? 'bg-primary text-white' : 'text-ink-subtle hover:text-ink',
                  )}
                >
                  {opt.icon}
                  {opt.label}
                </button>
              ))}
            </div>

            {mode === 'pin' ? (
              <Input
                label="PIN owner"
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                hint="Minta owner mengetikkan PIN persetujuannya di sini. Lima kali salah mengunci 10 menit."
                leftIcon={<KeyRound size={14} />}
              />
            ) : pending ? (
              <p className="inline-flex items-start gap-1.5 text-caption text-ink-subtle">
                <Clock size={14} className="mt-0.5 shrink-0" />
                <span>
                  Permintaan sudah dikirim {formatDateTime(pending.createdAt)} dan{' '}
                  <b>menunggu persetujuan</b>. Halaman ini memperbarui otomatis; begitu disetujui,
                  tombol Void bisa dipakai.
                </span>
              </p>
            ) : (
              <p className="text-caption text-ink-subtle">
                Permintaan masuk ke menu <b>Persetujuan</b> owner/admin. Kalau disetujui, kembali ke
                invoice ini dan klik Void.
              </p>
            )}
          </>
        )}

        {error && (
          <p role="alert" className="text-caption text-danger">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
